import { useState, useEffect, useRef, useCallback } from 'react'
import { toaster } from '@minetrace/ui'
import { ApiError } from '../api/client'
import { getErrorMessage } from '../api/errorCodes'

interface UseRequestConfig<TArg> {
  /** Fetch automatically on mount */
  onInit?: boolean
  /** Argument passed to fn when onInit is true */
  arg?: TArg
  /** Show error toast on failure (default: true) */
  showError?: boolean
  /** Show success toast with this message after execute */
  successMessage?: string
}

interface UseRequestResult<TData, TArg> {
  data: TData | undefined
  error: string | null
  isFetching: boolean
  success: boolean
  /** Call the wrapped function. Returns data on success, undefined on error. */
  execute: (arg: TArg) => Promise<TData | undefined>
  reset: () => void
}

export function useRequest<TData, TArg = void>(
  fn: (arg: TArg) => Promise<TData>,
  config?: UseRequestConfig<TArg>,
): UseRequestResult<TData, TArg> {
  const [data, setData] = useState<TData | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [success, setSuccess] = useState(false)
  const mountedRef = useRef(true)
  const fnRef = useRef(fn)
  fnRef.current = fn

  const showError = config?.showError !== false

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const execute = useCallback(
    async (arg: TArg): Promise<TData | undefined> => {
      setIsFetching(true)
      setError(null)
      setSuccess(false)

      try {
        const result = await fnRef.current(arg)
        if (!mountedRef.current) return undefined

        setData(result)
        setSuccess(true)

        if (config?.successMessage) {
          toaster.success({ description: config.successMessage })
        }

        return result
      } catch (err) {
        if (!mountedRef.current) return undefined

        let message = 'Ocurrio un error inesperado'
        if (err instanceof ApiError) {
          message = getErrorMessage(err.errorCode, err.message)
        } else if (err instanceof Error) {
          message = err.message
        }

        setError(message)

        if (showError) {
          toaster.error({ description: message })
        }

        return undefined
      } finally {
        if (mountedRef.current) {
          setIsFetching(false)
        }
      }
    },
    [showError, config?.successMessage],
  )

  const reset = useCallback(() => {
    setData(undefined)
    setError(null)
    setIsFetching(false)
    setSuccess(false)
  }, [])

  // Auto-fetch on mount
  useEffect(() => {
    if (config?.onInit) {
      execute(config.arg as TArg)
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { data, error, isFetching, success, execute, reset }
}
